import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { errorMessageKey, extractErrorCode, extractErrorMessage } from './error-messages';

/**
 * Central service for translating backend error codes to user-visible messages.
 * Uses Transloco to provide localized error messages.
 */
@Injectable({
  providedIn: 'root',
})
export class ErrorHandlerService {
  private readonly transloco = inject(TranslocoService);

  /**
   * Translate an HTTP error response to a user-visible message.
   * Extracts the error code, looks it up in the translation map,
   * and returns the localized message.
   *
   * Fallback chain:
   * 1. Translated message for the error code
   * 2. Original error message from backend (if any)
   * 3. Generic "Something went wrong" message
   */
  translateError(error: unknown): string {
    // Extract error code from the response
    const code = extractErrorCode(error);
    const key = errorMessageKey(code);

    // Translate using Transloco; if not loaded, the key is returned as-is
    const translated = this.transloco.translate(key);

    // If translation succeeded (returned something other than the key), use it
    if (translated && translated !== key) {
      return translated;
    }

    // Try to extract a message from the error itself
    const originalMessage = extractErrorMessage(error);
    if (originalMessage) {
      return originalMessage;
    }

    // Fall back to a generic message
    return this.transloco.translate('common.errors.unexpected');
  }
}
